import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Brain, Search, Compass, ArrowRight, BookOpen, Building, Home, Lock, Clock, CheckCircle, Download, Smartphone, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useReadingProgress } from '../contexts/ReadingProgressContext';

import { getStatusMessage, getStatusColor } from '../utils/moduleReleaseSystem';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

const Landing = () => {
  const { language } = useLanguage();
  const { isProgressLoading, getModuleReadingProgress } = useReadingProgress();
  const [modules, setModules] = useState([]);
  const [completedModules, setCompletedModules] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Install App Banner Component
  const InstallAppBanner = () => {
    const { language } = useLanguage();
    const [showBanner, setShowBanner] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);

    useEffect(() => {
      // Check if app is already installed
      const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone ||
                         document.referrer.includes('android-app://');
      
      if (isInstalled) return;

      // Check if banner was dismissed recently
      const dismissed = localStorage.getItem('install-banner-dismissed');
      const dismissedTime = dismissed ? new Date(dismissed) : null;
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      
      if (!dismissed || (dismissedTime && dismissedTime < oneDayAgo)) {
        setShowBanner(true);
      }

      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowBanner(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    const handleInstall = async () => {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        setShowBanner(false);
      } else {
        // Show instruction for manual install
        const message = language === 'en' 
          ? 'To install: Tap your browser menu (⋮) and select "Add to Home Screen" or "Install App"'
          : 'Para instalar: Toca el menú de tu navegador (⋮) y selecciona "Añadir a pantalla de inicio" o "Instalar app"';
        alert(message);
      }
    };

    const handleDismiss = () => {
      setShowBanner(false);
      localStorage.setItem('install-banner-dismissed', new Date().toISOString());
    };

    if (!showBanner) return null;

    return (
      <div className="mb-8">
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">
                    {language === 'en' ? 'Install Redwood Conecta' : 'Instalar Redwood Conecta'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'en' 
                      ? 'Get the app for offline access and a better experience'
                      : 'Obtén la app para acceso sin conexión y mejor experiencia'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={handleInstall} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Install' : 'Instalar'}
                </Button>
                <Button onClick={handleDismiss} variant="ghost" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const iconMap = {
    brain: Brain,
    search: Search,
    compass: Compass,
    school: BookOpen,
    landmark: Building,
    home: Home
  };

  const moduleColorMap = {
    'what-is-autism': 'from-orange-100 to-orange-200',
    'early-signs': 'from-green-100 to-green-200', 
    'first-steps': 'from-blue-100 to-blue-200',
    'school-system': 'from-purple-100 to-purple-200',
    'lanterman-act': 'from-teal-100 to-teal-200',
    'therapy-strategies': 'from-rose-100 to-rose-200'
  };

  const statusIcons = {
    available: null,
    locked: Lock,
    coming_soon: Clock,
    completed: CheckCircle
  };

  const staticContent = {
    en: {
      hero: {
        title: "Understanding Autism Together",
        subtitle: "Evidence-based resources for families beginning their autism journey",
        description: "Learn about autism, recognize early signs, and discover next steps with our comprehensive educational modules designed for families."
      },
      cta: "Start Learning",
      callToAction: {
        title: "Start Your Learning Journey Today",
        description: "Each module includes comprehensive information and an interactive quiz to test your understanding.",
        button: 'Begin with "What is Autism?"'
      }
    },
    es: {
      hero: {
        title: "Entendiendo el Autismo Juntos",
        subtitle: "Recursos basados en evidencia para familias que comienzan su viaje con el autismo",
        description: "Aprende sobre el autismo, reconoce señales tempranas y descubre los próximos pasos con nuestros módulos educativos integrales diseñados para familias."
      },
      cta: "Comenzar a Aprender",
      callToAction: {
        title: "Comienza tu Jornada de Aprendizaje Hoy",
        description: "Cada módulo incluye información integral y un cuestionario interactivo para evaluar tu comprensión.",
        button: 'Comenzar con "¿Qué es el Autismo?"'
      }
    }
  };

  const content = staticContent[language];

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('DEBUG: fetchModules is running');
      
      // Get full module data from API
      const response = await apiService.getModules();
        const fullModules = response.modules || [];
        console.log('DEBUG: Full modules from API:', fullModules);
        
        // Create a map of full modules by ID for easier lookup
        const moduleMap = {};
        fullModules.forEach(module => {
          moduleMap[module.moduleId] = module;
        });
        
        // SIMPLIFIED: All modules unlocked, focus on progress tracking only
        const simpleModules = fullModules.map((module, index) => {
          return {
            ...module,
            id: module.moduleId,
            order: index + 1,
            status: 'available', // All modules available
            isAccessible: true,  // All modules accessible
            isCompleted: false   // We'll track this separately
          };
        });
        
        console.log(`📊 LANDING: Created ${simpleModules.length} modules - all unlocked for progress tracking focus`);
        
        console.log('Simplified modules with completion status:', simpleModules);
        setModules(simpleModules);
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <LoadingSpinner 
          size="large" 
          text={language === 'en' ? 'Loading modules...' : 'Cargando módulos...'}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <ErrorMessage error={error} onRetry={fetchModules} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

      <div className="text-center py-16 sm:py-20">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {content.hero.title}
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 mb-8 leading-relaxed">
            {content.hero.subtitle}
          </p>
          <p className="text-lg text-gray-500 mb-12 max-w-3xl mx-auto leading-relaxed">
            {content.hero.description}
          </p>
        </div>
      </div>

      {/* PWA Install Banner */}
      <InstallAppBanner />

      {/* Modules Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {modules.map((module, index) => {
          if (!module) return null;
          
          const IconComponent = iconMap[module.icon];
          const StatusIcon = statusIcons[module.status];
          const colorClasses = moduleColorMap[module.moduleId] || 'from-orange-100 to-orange-200';
          const statusColorClasses = getStatusColor(module.status);
          const readingProgress = getModuleReadingProgress(module.moduleId);
          
          return (
            <Card 
              key={module.moduleId} 
              className={`group transition-all duration-300 transform ${
                module.isAccessible 
                  ? 'hover:shadow-xl hover:-translate-y-2 cursor-pointer' 
                  : 'opacity-75 cursor-not-allowed'
              } border-0 bg-gradient-to-br ${module.isAccessible ? colorClasses : statusColorClasses} backdrop-blur-sm`}
            >
              <CardHeader className="text-center pb-4">
                <div className="relative">
                  <div className={`w-16 h-16 mx-auto mb-4 bg-white/80 rounded-2xl flex items-center justify-center ${
                    module.isAccessible ? 'group-hover:scale-110' : ''
                  } transition-transform duration-300`}>
                    <IconComponent className="w-8 h-8 text-gray-700" />
                  </div>
                  
                  {/* Status Indicator */}
                  {StatusIcon && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                      <StatusIcon className="w-4 h-4 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Module Number */}
                  <div className="absolute -top-1 -left-1 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {module.order}
                  </div>
                </div>
                
                <CardTitle className="text-xl font-bold text-gray-800 mb-2">
                  {module.title[language]}
                </CardTitle>
                <CardDescription className="text-gray-600 text-base leading-relaxed mb-3">
                  {module.description[language]}
                </CardDescription>
                
                {/* Status Message */}
                <div className={`text-sm font-medium mb-3 ${
                  module.status === 'completed' ? 'text-green-700' :
                  module.status === 'available' ? 'text-blue-700' :
                  module.status === 'locked' ? 'text-yellow-700' : 'text-gray-600'
                }`}>
                  {getStatusMessage(module.status, language)}
                </div>
                
                {/* Reading Progress for Available/Completed Modules */}
                {module.isAccessible && readingProgress.totalSections > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{language === 'en' ? 'Progress' : 'Progreso'}</span>
                      <span>{readingProgress.sectionsRead}/{readingProgress.totalSections}</span>
                    </div>
                    <Progress value={readingProgress.progressPercentage} className="h-2 bg-white/50" />
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {/* SIMPLIFIED: All modules accessible - focus on progress display */}
                <Link to={`/module/${module.moduleId}`}>
                  <Button 
                    className="w-full bg-white/90 hover:bg-white text-gray-800 border-0 shadow-sm hover:shadow-md transition-all duration-200 group"
                  >
                    {language === 'en' ? 'Start Learning' : 'Comenzar a Aprender'}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Call to Action */}
      <div className="text-center py-12 bg-gradient-to-r from-orange-100 via-green-100 to-blue-100 rounded-3xl mb-16">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          {content.callToAction.title}
        </h2>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          {content.callToAction.description}
        </p>
        <Link to="/module/what-is-autism">
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
          >
            {content.callToAction.button}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Landing;